import { STATES_ALLOWING_FILL } from "PFApp/constants/shortlist_states";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { RoleFormKey, RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useInvalidateManageViewRoles } from "PFCore/hooks/queries/roles/use_invalidate_manage_view_roles";
import { useShortlistFillAndBook } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { AvailabilityRule, Subtemplate } from "PFCore/types";
import { RefCallback, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  MANAGE_ROLES_SYNC_ERROR_GROWL_ID,
  MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID
} from "../hooks/use_unsaved_edits_sync";
import { EditAvailabilitySidePanel } from "./edit_availability_side_panel";
import { ManageRolesTables } from "./manage_roles_tables";
import { useUnsavedEditsSyncContext } from "./unsaved_edits_sync_context";

type ManageRolesAccordionContentProps = {
  rolesFormKey: RolesFormKey;
  subtemplate: Subtemplate | null;
  leftPanelRef: RefCallback<HTMLDivElement>;
};

export const ManageRolesAccordionContent = ({
  rolesFormKey,
  subtemplate,
  leftPanelRef
}: ManageRolesAccordionContentProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });
  const { showGrowl } = useGrowlStoreActions();

  const { getValues } = useFormContext<ManageRolesMultiForm>();
  const { invalidateAll } = useInvalidateManageViewRoles();
  const { flushUnsavedEdits } = useUnsavedEditsSyncContext();
  const { mutateAsync: fillAndBookShortlist } = useShortlistFillAndBook();
  const { update: updateActivity } = useActivityUpdate();

  const [editingRoleFormKey, setEditingRoleFormKey] = useState<RoleFormKey | null>(null);

  const handleEditAvailabilityApply = useCallback(
    async (roleFormKey: RoleFormKey, availability: AvailabilityRule) => {
      const roleValues = getValues(roleFormKey) as ManageRolesFormType;
      const roleId = roleValues?.id;

      if (!roleId) {
        return;
      }

      await flushUnsavedEdits();

      try {
        await updateActivity({ id: Number(roleId), metadata: { availability } });
        showGrowl({
          message: t("saveSuccess", { count: 1 }),
          kind: "success",
          id: MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
        await invalidateAll();
        setEditingRoleFormKey(null);
      } catch {
        // Keep the side panel open on failure so the user can retry. The server state is
        // unchanged, so there is nothing to refetch.
        showGrowl({
          message: t("saveError"),
          kind: "error",
          id: MANAGE_ROLES_SYNC_ERROR_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
      }
    },
    [flushUnsavedEdits, getValues, invalidateAll, showGrowl, t, updateActivity]
  );

  const handleEditAvailabilityClose = useCallback(() => setEditingRoleFormKey(null), []);

  const handlePublish = useCallback(
    async (roleFormKey: RoleFormKey) => {
      const roleValues = getValues(roleFormKey) as ManageRolesFormType;
      const roleId = roleValues?.id;

      if (!roleId) {
        return;
      }

      const allSaved = await flushUnsavedEdits();
      if (!allSaved) {
        return;
      }

      try {
        await updateActivity({ id: Number(roleId), state: "new" });
        showGrowl({
          message: t("publishSuccess"),
          kind: "success",
          id: MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
        await invalidateAll();
      } catch {
        showGrowl({
          message: t("publishError"),
          kind: "error",
          id: MANAGE_ROLES_SYNC_ERROR_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
      }
    },
    [flushUnsavedEdits, getValues, invalidateAll, showGrowl, t, updateActivity]
  );

  const handleFillAndBook = useCallback(
    async (roleFormKey: RoleFormKey) => {
      const roleValues = getValues(roleFormKey) as ManageRolesFormType;
      const roleId = roleValues?.id;
      const fillableWm = (roleValues?.suggested_wm ?? []).filter((profile) =>
        STATES_ALLOWING_FILL.includes(profile.shortlist_state)
      );

      if (!roleId || fillableWm.length === 0) {
        return;
      }

      const allSaved = await flushUnsavedEdits();
      if (!allSaved) {
        return;
      }

      const results = await Promise.allSettled(
        fillableWm.map((profile) =>
          fillAndBookShortlist({
            shortlistId: profile.shortlist_id,
            profileId: Number(profile.id),
            activity: { id: Number(roleId), parent_activity_id: roleValues.parent_activity?.id ?? null }
          })
        )
      );

      const failuresCount = results.filter((result) => result.status === "rejected").length;
      const successesCount = results.length - failuresCount;

      if (successesCount > 0) {
        showGrowl({
          message: t("granularFillAndBookSuccess", { count: successesCount }),
          kind: "success",
          id: MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
        await invalidateAll();
      }
      if (failuresCount > 0) {
        showGrowl({
          message: t("granularFillAndBookError"),
          kind: "error",
          id: MANAGE_ROLES_SYNC_ERROR_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
      }
    },
    [fillAndBookShortlist, flushUnsavedEdits, getValues, invalidateAll, showGrowl, t]
  );

  return (
    <>
      <ManageRolesTables
        rolesFormKey={rolesFormKey}
        subtemplate={subtemplate}
        leftPanelRef={leftPanelRef}
        onEditAvailability={setEditingRoleFormKey}
        onPublish={handlePublish}
        onFillAndBook={handleFillAndBook}
      />
      <EditAvailabilitySidePanel
        show={!!editingRoleFormKey}
        roleFormKey={editingRoleFormKey}
        onClose={handleEditAvailabilityClose}
        onApply={handleEditAvailabilityApply}
      />
    </>
  );
};
